import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';

// Shared-rig contract: forbidden. There is one light, it is off screen, and
// the only thing it ever touches is the water's surface.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'steam',
  family: 'organic',
  danceability: 0.12,
  energyLevel: 0.12,
  bpmMin: 40,
  bpmMax: 115,
  movement: 0.25,
  tonality: 0.95,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.35] },
  enabled: true
};

// Steam: still water seen from directly above, and drops falling into it.
//
// THAT IS THE WHOLE PIECE. There is no horizon, no scene and nothing to look
// past: the camera points straight down at a dark surface, drops come down out
// of the dark, and every one of them leaves a ring that spreads, crosses the
// rings already there, and dies. What you watch is the interference.
//
// Everything follows from looking STRAIGHT DOWN. There is no perspective, so
// the simulation grid maps one to one onto the frame and a ring drawn round
// stays round. There is no sky to reflect, so the surface is read entirely
// from its own slope: a specular from one off-screen light, the bed refracted
// through the water, and the caustics the wave's own curvature focuses onto
// that bed. Those three are the only reason a flat dark rectangle reads as
// water at all.
//
// NOTHING IS FAST. A ring takes about three seconds to cross the frame and a
// drop about a second to arrive. There is no pulse anywhere in this file and
// nothing is locked to the beat.

// --- the ripple field ---------------------------------------------------------
// A damped wave equation. C2 is the Courant term: the five-point Laplacian is
// stable to 0.5, and 0.28 carries a front across the grid in about three
// seconds at 60 fps.
const SIM_C2 = 0.28;
const SIM_DAMP = 0.9955;
const IMPULSE_MAX = 192;

// --- the drops ----------------------------------------------------------------
const DROP_RATE = [0.9, 4.5];    // per second, from bands.low
const DROP_AMP = [0.020, 0.055]; // small ones are far more common

// --- the surface --------------------------------------------------------------
// THREE DIFFERENT GAINS ON THE SAME FIELD, because they answer three
// different questions. The height says how high the water is (small), the
// refraction says how far the bed slides under it (small), and the NORMAL
// says how steeply the surface is tilted - and that one has to be large,
// because the field's gradient between two neighbouring texels is about three
// ten-thousandths and a normal tilted by that is indistinguishable from flat.
const HEIGHT_GAIN = 16.0;
const REFRACT_GAIN = 2.2;
const NORMAL_GAIN = 300.0;
const CAUSTIC_GAIN = 0.21;
// The key is well off to one side and fairly low: a light straight overhead
// puts the same highlight on every part of a flat surface and the ripples
// stop being the thing that draws it.
const LIGHT_DIR = [0.55, 0.70, 0.45];
const SAFE_FLOOR = 0.55;
const PEAK = 0.97;

const TIER = {
  0: { simW: 160, simH: 90, drops: 0.60 },
  1: { simW: 224, simH: 126, drops: 0.80 },
  2: { simW: 288, simH: 162, drops: 1.00 }
};
const DEFAULT_TIER = 2;

const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
const _size = new THREE.Vector2();

function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }
function rand(a, b) { return a + Math.random() * (b - a); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function numParam(name) {
  const v = parseFloat(urlParam(name));
  return Number.isFinite(v) ? v : null;
}

// =============================================================================
// SHADERS
// =============================================================================

const FULL_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// r = height now, g = height a frame ago, ba = the surface gradient.
//
// THE GRADIENT IS WRITTEN HERE and not recomputed when the surface is drawn.
// The simulation already holds the four neighbours in registers; the display
// pass runs at several times this resolution and would need a bilinear fetch
// per neighbour - sixteen taps - to get the same number smoothly. Written into
// b and a it comes back in the same four taps as the height itself.
const SIM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uPrev;
  uniform vec2 uTexel;
  uniform float uFrozen;
  varying vec2 vUv;
  void main() {
    vec4 c = texture2D(uPrev, vUv);
    if (uFrozen > 0.5) { gl_FragColor = c; return; }
    float n = texture2D(uPrev, vUv + vec2(0.0, uTexel.y)).r;
    float s = texture2D(uPrev, vUv - vec2(0.0, uTexel.y)).r;
    float e = texture2D(uPrev, vUv + vec2(uTexel.x, 0.0)).r;
    float w = texture2D(uPrev, vUv - vec2(uTexel.x, 0.0)).r;
    // NINE POINTS, NOT FIVE. The five-point Laplacian is anisotropic on a
    // square grid: it carries a wave faster along the axes than along the
    // diagonals, and a drop spreads as a rounded SQUARE rather than a circle -
    // which is exactly what the first pass of this looked like. The diagonals
    // at a quarter weight cancel that to first order, and on a grid this small
    // four extra taps cost nothing.
    float ne = texture2D(uPrev, vUv + uTexel).r;
    float nw = texture2D(uPrev, vUv + vec2(-uTexel.x, uTexel.y)).r;
    float se = texture2D(uPrev, vUv + vec2(uTexel.x, -uTexel.y)).r;
    float sw = texture2D(uPrev, vUv - uTexel).r;
    float lap = 0.5 * (n + s + e + w) + 0.25 * (ne + nw + se + sw) - 3.0 * c.r;
    float nx = (2.0 * c.r - c.g + ${SIM_C2.toFixed(3)} * lap) * ${SIM_DAMP.toFixed(4)};
    // The border absorbs instead of reflecting. There is no basin here and no
    // edge to see, so a ring bouncing off the frame would be a ring bouncing
    // off nothing, and four of them would set up a standing pattern that still
    // water never has.
    float edge = min(min(vUv.x, 1.0 - vUv.x), min(vUv.y, 1.0 - vUv.y));
    // A wide, gentle taper. At 0.07 the amplitude fell off over three texels
    // and the gradient across that step lit a white frame all the way round
    // the picture - the absorbing boundary drawing itself.
    nx *= smoothstep(0.0, 0.16, edge);
    gl_FragColor = vec4(nx, c.r, (e - w) * 0.5, (n - s) * 0.5);
  }
`;

// A drop landing. NEGATIVE and equal in r and g: a drop is an initial
// DISPLACEMENT with no velocity, and setting the two time steps apart would
// inject momentum the drop never had.
const IMPULSE_VERTEX = `
  precision mediump float;
  attribute float aAmp;
  uniform float uPixel;
  varying float vAmp;
  void main() {
    vAmp = aAmp;
    gl_Position = vec4(position.xy, 0.0, 1.0);
    gl_PointSize = uPixel;
  }
`;
const IMPULSE_FRAGMENT = `
  precision mediump float;
  varying float vAmp;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    float a = exp(-r2 * 11.0) * vAmp;
    gl_FragColor = vec4(-a, -a, 0.0, 0.0);
  }
`;

// The surface. Everything here is read from the slope, because looking
// straight down at water there is nothing else to read.
const WATER_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uField;
  uniform vec2 uFieldTexel;
  uniform float uHasField;
  uniform vec3 uDeep;
  uniform vec3 uBed;
  uniform vec3 uLight;
  uniform float uTime;
  uniform float uAspect;         // the safe-zone chunk declares it, but only
                                 // in the vertex stage: a different program
  uniform float uSharp;          // bands.high: how tight the specular is
  uniform float uShowField;
  varying vec2 vUv;
  varying float vSafe;

  float hash12(vec2 p) {
    vec3 q = fract(vec3(mod(p.x, 512.0), mod(p.y, 512.0), mod(p.x, 512.0)) * 0.1031);
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),
               mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  // Manual bilinear: the field is float, and V3D renders to float but cannot
  // FILTER it. Four taps, bringing back the gradient in ba at the same time as
  // the height in r.
  vec4 fieldAt(vec2 uv) {
    vec2 t = uv / uFieldTexel - 0.5;
    vec2 i = floor(t), f = fract(t);
    vec2 b = (i + 0.5) * uFieldTexel;
    vec4 c00 = texture2D(uField, b);
    vec4 c10 = texture2D(uField, b + vec2(uFieldTexel.x, 0.0));
    vec4 c01 = texture2D(uField, b + vec2(0.0, uFieldTexel.y));
    vec4 c11 = texture2D(uField, b + uFieldTexel);
    return mix(mix(c00, c10, f.x), mix(c01, c11, f.x), f.y);
  }

  void main() {
    // STRAIGHT DOWN, so the grid maps one to one onto the frame: no
    // perspective term anywhere, and a ring drawn round stays round.
    vec4 fd = uHasField > 0.5 ? fieldAt(vUv) : vec4(0.0);
    float h = fd.r * ${HEIGHT_GAIN.toFixed(1)};
    vec2 g = fd.ba;

    if (uShowField > 0.5) {
      // ?steamfield=1 - the raw height, so a ring can be read straight off the
      // simulation instead of inferred from the lighting drawn on top of it.
      gl_FragColor = vec4(vec3(0.5) + vec3(h * 4.0, h * 4.0, -h * 4.0), 1.0);
      return;
    }

    vec2 p = vec2((vUv.x - 0.5) * uAspect, vUv.y - 0.5);

    // THE BED, seen through the water and displaced by the slope. Refraction
    // is what says there is a THICKNESS of water between you and the bottom;
    // without it the rings look like they are drawn on a wall.
    vec2 bp = p + g * ${REFRACT_GAIN.toFixed(2)};
    float bed = vnoise(bp * 9.0) * 0.55 + vnoise(bp * 23.0 + 4.0) * 0.45;
    // A QUIET bed. Its mottling is there to give the water some body, not to
    // compete: at a third of the way to uBed the blotches read as loudly as
    // the rings do, and the rings are the subject.
    vec3 col = mix(uDeep, uBed, smoothstep(0.38, 0.80, bed) * 0.18);

    vec3 nrm = normalize(vec3(-g.x * ${NORMAL_GAIN.toFixed(1)},
                              -g.y * ${NORMAL_GAIN.toFixed(1)}, 1.0));
    vec3 L = normalize(vec3(${LIGHT_DIR[0].toFixed(2)}, ${LIGHT_DIR[1].toFixed(2)},
                            ${LIGHT_DIR[2].toFixed(2)}));

    // THE CAUSTICS. Where the surface curves like a lens it focuses the light
    // onto the bed, and where it curves the other way it spreads it. That
    // curvature is the DIVERGENCE of the gradient, and the hardware hands it
    // over for almost nothing: screen derivatives are computed per 2x2 quad
    // anyway, so this costs two instructions rather than four more taps.
    // Capped hard: at pow(clamp(x,0,3), 3) this term alone reached 2.9 on a
    // surface whose whole base colour is under 0.2, and the frame went white.
    {
      // THE FOCUS COMES FROM THE HEIGHT, NOT FROM A SCREEN DERIVATIVE. For a
      // thin lens the brightness on the bed goes as -laplacian(h), and for a
      // ripple field that is proportional to -h, so the trough is where the
      // light gathers. Taking the divergence with dFdx/dFdy is the same
      // quantity in principle and unusable in practice: the field is magnified
      // four times, its bilinear interpolation has a DISCONTINUOUS derivative
      // at every texel boundary, and the screen derivative of it came back as
      // a regular lattice of spikes - the wavefronts arrived visibly beaded.
      float focus = clamp(-h * 5.5, 0.0, 2.2);
      col += uLight * focus * focus * ${CAUSTIC_GAIN.toFixed(2)};
    }

    // THE BROAD SHADING is what actually draws a ring. The exponent does two
    // jobs at once: it darkens flat water, which returns only 0.455 of the
    // key at this angle, and it steepens the response so a slope of a few
    // degrees changes the brightness by a factor of two or three. A readable
    // modulation, rather than a razor highlight that is either absent or blown
    // out.
    float lam = max(dot(nrm, L), 0.0);
    col += uLight * pow(lam, 4.0) * 0.32;

    // And a narrow specular on top for the glints on the steepest crests.
    vec3 H = normalize(L + vec3(0.0, 0.0, 1.0));
    float spec = pow(max(dot(nrm, H), 0.0), mix(26.0, 90.0, uSharp));
    col += uLight * spec * 0.36;

    // The troughs go darker than the crests, which gives a ring its near side
    // and its far side.
    col *= 1.0 + clamp(h * 1.2, -0.45, 0.45);

    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) * (2.0 / 255.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, vSafe);
    gl_FragColor = vec4(clamp(col, 0.0, ${PEAK.toFixed(2)}), 1.0);
  }
`;

const WATER_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  varying float vSafe;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy * 2.0, 0.0, 1.0);
    vSafe = clamp((safeZoneAttenuation(clip) - 0.32) / 0.68, 0.0, 1.0);
    gl_Position = clip;
  }
`;

// =============================================================================

export class SteamVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._lastPalette = null;
    this._impulseCount = 0;
    this._dropAcc = 0;

    this._forceRate = numParam('steamdrops');
    this._single = urlParam('steamsingle') === '1';
    this._showField = urlParam('steamfield') === '1';
  }

  init(scene) {
    this._scene = scene;
    const L = this._layout;

    this._deep = new THREE.Vector3(0.012, 0.025, 0.043);
    this._bed = new THREE.Vector3(0.060, 0.095, 0.130);
    this._light = new THREE.Vector3(0.82, 0.88, 1.00);

    this._fieldTex = { value: null };
    this._hasField = { value: 0 };
    this._fieldTexel = { value: new THREE.Vector2(1 / L.simW, 1 / L.simH) };
    this._timeU = { value: 0 };

    const mat = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, safeZoneUniforms, {
        uField: this._fieldTex,
        uFieldTexel: this._fieldTexel,
        uHasField: this._hasField,
        uDeep: { value: this._deep },
        uBed: { value: this._bed },
        uLight: { value: this._light },
        uTime: this._timeU,
        uSharp: { value: 0.5 },
        uShowField: { value: this._showField ? 1 : 0 }
      }),
      vertexShader: WATER_VERTEX,
      fragmentShader: WATER_FRAGMENT,
      depthTest: false,
      depthWrite: false,
      // WebGL1 (which the TV may still be) needs this declared for dFdx/dFdy.
      extensions: { derivatives: true }
    });
    this._water = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._water.frustumCulled = false;
    this._water.renderOrder = -100;
    scene.add(this._water);

    if (FLOAT_TEXTURES_OK) this._buildSim(L);

    // The render-target work has to run from inside the renderer's own pass -
    // the contract Echo established - so the water quad drives the simulation.
    const self = this;
    this._water.onBeforeRender = function (renderer) {
      self._runSim(renderer);
    };
  }

  _buildSim(L) {
    this._simW = L.simW;
    this._simH = L.simH;
    this._field = new FeedbackBuffer({
      width: this._simW, height: this._simH,
      type: THREE.FloatType, filter: THREE.NearestFilter
    });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);

    this._simUniforms = {
      uPrev: { value: null },
      uTexel: { value: new THREE.Vector2(1 / this._simW, 1 / this._simH) },
      uFrozen: { value: 0 }
    };
    this._simMat = new THREE.ShaderMaterial({
      uniforms: this._simUniforms,
      vertexShader: FULL_VERTEX,
      fragmentShader: SIM_FRAGMENT,
      depthTest: false, depthWrite: false
    });
    this._simScene = new THREE.Scene();
    const q = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._simMat);
    q.frustumCulled = false;
    this._simScene.add(q);

    const g = new THREE.BufferGeometry();
    this._impPos = new Float32Array(IMPULSE_MAX * 3);
    this._impAmp = new Float32Array(IMPULSE_MAX);
    g.setAttribute('position', new THREE.BufferAttribute(this._impPos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aAmp', new THREE.BufferAttribute(this._impAmp, 1).setUsage(THREE.DynamicDrawUsage));
    g.setDrawRange(0, 0);
    this._impGeo = g;
    this._impMat = new THREE.ShaderMaterial({
      uniforms: { uPixel: { value: Math.max(3, this._simW / 52) } },
      vertexShader: IMPULSE_VERTEX,
      fragmentShader: IMPULSE_FRAGMENT,
      transparent: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      depthTest: false, depthWrite: false
    });
    this._impScene = new THREE.Scene();
    const pts = new THREE.Points(g, this._impMat);
    pts.frustumCulled = false;
    this._impScene.add(pts);
  }

  _runSim(renderer) {
    if (!this._field) return;
    this._field.ensureSize(renderer);
    const prevTarget = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    this._simUniforms.uPrev.value = this._field.read.texture;
    this._simUniforms.uFrozen.value = this._paused ? 1 : 0;
    renderer.setRenderTarget(this._field.write);
    renderer.autoClear = false;
    renderer.render(this._simScene, this._ortho);
    if (this._impulseCount > 0 && !this._paused) {
      this._impGeo.setDrawRange(0, this._impulseCount);
      this._impGeo.attributes.position.needsUpdate = true;
      this._impGeo.attributes.aAmp.needsUpdate = true;
      renderer.render(this._impScene, this._ortho);
    }
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAuto;
    this._field.swap();
    this._fieldTex.value = this._field.read.texture;
    this._hasField.value = 1;
    this._impulseCount = 0;
  }

  // Screen position straight into the field: looking down, the two are the
  // same space, which is the whole simplification of this view.
  _ripple(x, v, amp) {
    if (!this._field || this._impulseCount >= IMPULSE_MAX) return;
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const i = this._impulseCount++;
    this._impPos[i * 3] = (x / aspect) * 2;
    this._impPos[i * 3 + 1] = v * 2 - 1;
    this._impPos[i * 3 + 2] = 0;
    this._impAmp[i] = amp;
  }

  _spawnDrop() {
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    // Small drops are far more common than big ones, which is what keeps the
    // surface from settling into a regular pattern of equal rings.
    const big = Math.pow(Math.random(), 2.4);
    this._ripple(rand(-aspect * 0.5, aspect * 0.5), rand(0.02, 0.98),
                 DROP_AMP[0] + (DROP_AMP[1] - DROP_AMP[0]) * big);
  }

  update(deltaTime, profile) {
    // PAUSE IS A COMPLETE FREEZE: the drops hang where they are and the
    // simulation holds its state rather than relaxing to flat.
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._timeU.value = this._time;

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    const low = clamp01(bands.low), high = clamp01(bands.high);

    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPalette) {
      this._lastPalette = palette;
      this._applyPalette(palette);
    }
    this._water.material.uniforms.uSharp.value = high;

    // bands.low decides how hard it is raining, and that is the only thing the
    // music moves here.
    const rate = this._forceRate != null ? this._forceRate
      : (DROP_RATE[0] + (DROP_RATE[1] - DROP_RATE[0]) * low) * this._layout.drops;
    if (this._single) {
      // ?steamsingle=1 - one drop every eight seconds, dead centre, so a
      // single ring can be watched from its birth to the far edge with nothing
      // else on the water.
      this._dropAcc += dt;
      if (this._dropAcc >= 8) {
        this._dropAcc = 0;
        this._ripple(0, 0.5, DROP_AMP[1]);
      }
    } else {
      this._dropAcc += dt * rate;
      while (this._dropAcc >= 1) { this._dropAcc -= 1; this._spawnDrop(); }
    }
  }

  // THE ARTWORK IS THE WATER'S COLOUR. It was already driving the hue exactly
  // - measured at 234 degrees on a blue sleeve, 122 on a green one, 359 on a
  // red one - and none of it could be seen, because the surface sat at a
  // luminance of 0.04, which is eight to fifteen out of 255. A colour that
  // dark is black whatever its hue. The mapping did not need changing; the
  // surface needed to be bright enough to show it.
  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0x2b4a6b];
    _col.set(vivid[0]);
    _col.getHSL(_hsl);
    // A floor as well as a cap, so a washed-out sleeve still gives water a
    // colour rather than a neutral slab - but the floor CANNOT be applied to a
    // grey, whose hue is 0 by convention and not by measurement: forcing
    // saturation onto it turned a grey sleeve's water red.
    const s = _hsl.s < 0.06 ? 0.42 : Math.max(0.30, Math.min(_hsl.s, 0.72));
    if (_hsl.s < 0.06) _hsl.h = 0.58;   // a neutral sleeve gives plain water
    const h = _hsl.h;
    const deep = new THREE.Color().setHSL(h, s * 0.85, 0.048);
    const bed = new THREE.Color().setHSL(h, s * 0.62, 0.115);
    // The light keeps a trace of the sleeve and stays close to white: a
    // strongly tinted specular stops reading as light on water.
    const lit = new THREE.Color().setHSL(h, s * 0.22, 0.900);
    this._deep.set(deep.r, deep.g, deep.b);
    this._bed.set(bed.r, bed.g, bed.b);
    this._light.set(lit.r, lit.g, lit.b);
  }

  dispose() {
    const scene = this._scene;
    for (const m of [this._water]) {
      if (!m) continue;
      if (scene) scene.remove(m);
      m.material.dispose();
      m.geometry.dispose();
    }
    if (this._water) this._water.onBeforeRender = function () {};
    if (this._field) this._field.dispose();
    if (this._simMat) this._simMat.dispose();
    if (this._impMat) { this._impMat.dispose(); this._impGeo.dispose(); }
    this._water = null;
    this._field = null;
  }
}
